/**
 * Signup attribution — remembers how a visitor arrived, so the account they
 * eventually create carries its own origin.
 *
 * WHY THIS EXISTS: signup (Signup.tsx) captured nothing. A Google-SSO signup
 * produced an account row with no referrer, no campaign, no landing page, and
 * the pre-signup funnel tables (leads / funnel_events) only see the Instant
 * Report path — so anyone who lands straight on /signup is invisible. The
 * first real inbound signup (2026-08-26) was untraceable for exactly this
 * reason.
 *
 * HOW IT FLOWS:
 *   1. captureAttribution() runs on the FIRST app load of a browser session
 *      (main.tsx), while the URL still carries ?utm_* and document.referrer
 *      still points at wherever they came from. Later navigations no-op —
 *      first touch wins, so /signup doesn't overwrite /pricing?utm_source=x.
 *   2. It parks the result in sessionStorage. sessionStorage survives the
 *      OAuth round-trip (same tab, same origin, even though Google sits in
 *      the middle) and dies with the tab — no durable cross-visit identifier.
 *   3. api.ts attaches it as X-Waypoint-Attribution on authenticated calls.
 *   4. The very first authenticated call is the one that provisions the
 *      workspace (ensureAccount in apps/api/src/infra/auth.ts), so the header
 *      arrives exactly when there's a new account row to stamp. The server
 *      only reads it at creation time and ignores it forever after.
 *   5. clearAttribution() drops it once a call has succeeded, so it rides
 *      along on ~1-2 requests rather than every request in the session.
 *
 * PRIVACY (mirrors the consent split already used by InstantReport.tsx):
 *   - UTM tags, ad click IDs, and the landing path come out of OUR OWN URL,
 *     which the visitor deliberately navigated to. No third party, no
 *     cross-site profile — captured unconditionally and stored against an
 *     identified customer account.
 *   - document.referrer names an EXTERNAL site and is the one signal that
 *     reveals off-site browsing, so it is gated behind hasAnalyticsConsent()
 *     exactly like the Instant Report funnel. GPC is honored by that helper.
 *   - The stored blob records `consent` so an empty referrer is legible as
 *     "visitor declined", not "tracking is broken".
 *
 * CONSENT ARRIVES LATE (2026-09-02): first touch happens on page load, but the
 * cookie banner is answered seconds later. Anyone who lands and THEN accepts
 * analytics had their referrer thrown away a moment before they agreed to it —
 * which is every consenting visitor, i.e. the signal was structurally always
 * missing. So the referrer is now held in a MODULE VARIABLE (never storage,
 * never a cookie, never sent anywhere) and written into the stored blob only
 * if the visitor subsequently accepts. Pre-consent it lives in the tab's heap
 * for as long as the tab does and dies with it: nothing persists, nothing
 * leaves the browser, so this is a strictly later write of a value the visitor
 * has by then consented to — not an earlier collection of one they hadn't.
 */
import { hasAnalyticsConsent } from '../components/layout/CookieBanner';

const KEY = 'waypoint.attribution';
const CONSENT_CHANGED_EVENT = 'cookie-consent:changed';

/** Referrer observed at first touch while consent was ABSENT. In-memory only. */
let heldReferrer: string | null = null;
/** The consent listener is installed once per page load, not once per call. */
let consentListenerInstalled = false;

/** UTM keys we accept, mapped to the short form we store. */
const UTM_KEYS = [
  ['utm_source', 'source'],
  ['utm_medium', 'medium'],
  ['utm_campaign', 'campaign'],
  ['utm_term', 'term'],
  ['utm_content', 'content'],
] as const;

/** Ad-platform click identifiers — the only proof a signup came from paid. */
const CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid', 'li_fat_id', 'ttclid'] as const;

const MAX_VALUE = 200;

export interface Attribution {
  /** Path on our own site where the session started, e.g. "/pricing". No query string. */
  landing_path: string | null;
  /** { source, medium, campaign, term, content } — only keys actually present. */
  utm: Record<string, string>;
  /** { gclid, fbclid, ... } — only keys actually present. */
  click_ids: Record<string, string>;
  /** External referring URL. null when same-origin, absent, or consent withheld. */
  referrer: string | null;
  /** Whether analytics consent was granted at capture time (explains a null referrer). */
  consent: boolean;
  /** ISO timestamp of first touch. */
  captured_at: string;
}

function clamp(v: string): string {
  return v.trim().slice(0, MAX_VALUE);
}

function readParams(search: string): { utm: Record<string, string>; click_ids: Record<string, string> } {
  const params = new URLSearchParams(search);
  const utm: Record<string, string> = {};
  for (const [param, short] of UTM_KEYS) {
    const v = params.get(param);
    if (v && v.trim()) utm[short] = clamp(v);
  }
  const click_ids: Record<string, string> = {};
  for (const k of CLICK_ID_KEYS) {
    const v = params.get(k);
    if (v && v.trim()) click_ids[k] = clamp(v);
  }
  return { utm, click_ids };
}

/**
 * document.referrer, but only when it points somewhere other than us —
 * an internal navigation referrer is noise, not attribution.
 */
function readExternalReferrer(): string | null {
  try {
    const raw = document.referrer;
    if (!raw) return null;
    if (new URL(raw).origin === window.location.origin) return null;
    return raw.slice(0, 500);
  } catch {
    return null;
  }
}

/**
 * Capture first-touch attribution for this browser session. Idempotent and
 * cheap: after the first call in a session it returns immediately, so calling
 * it on every app boot is fine.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;
  // Installed BEFORE the first-touch early-return: a later app boot in the same
  // session must still be listening when the visitor answers the banner.
  installConsentUpgradeListener();
  try {
    if (sessionStorage.getItem(KEY)) return; // first touch already recorded

    const { utm, click_ids } = readParams(window.location.search);
    const consent = hasAnalyticsConsent();

    const referrer = readExternalReferrer();
    // Held, not stored, when consent is absent — see the CONSENT ARRIVES LATE
    // note above. Cleared the moment it is either used or refused.
    heldReferrer = consent ? null : referrer;

    const attribution: Attribution = {
      landing_path: window.location.pathname.slice(0, MAX_VALUE) || null,
      utm,
      click_ids,
      referrer: consent ? referrer : null,
      consent,
      captured_at: new Date().toISOString(),
    };

    sessionStorage.setItem(KEY, JSON.stringify(attribution));
  } catch {
    // Storage disabled (private mode, hardened browser). Attribution is a
    // nice-to-have — never let it break app boot.
  }
}

/**
 * Promote the in-memory referrer into the stored blob once the visitor accepts
 * analytics cookies. No-op when there is nothing held, when nothing was
 * captured, or when the blob already records consent.
 *
 * Exported for its test — the whole point of this function is a privacy rule,
 * and a privacy rule nobody asserts on is a comment.
 */
export function applyConsentUpgrade(): void {
  if (typeof window === 'undefined') return;
  try {
    if (!heldReferrer) return;
    if (!hasAnalyticsConsent()) return;
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return;
    const stored = JSON.parse(raw) as Attribution;
    if (stored.consent && stored.referrer) return;
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ ...stored, referrer: heldReferrer, consent: true } satisfies Attribution),
    );
  } catch {
    /* storage or JSON failure — attribution is a nice-to-have */
  } finally {
    // Either it was written or it never will be. Don't keep holding it.
    heldReferrer = null;
  }
}

/** Subscribe to the cookie banner's decision. Idempotent. */
function installConsentUpgradeListener(): void {
  if (consentListenerInstalled) return;
  consentListenerInstalled = true;
  try {
    window.addEventListener(CONSENT_CHANGED_EVENT, (e) => {
      // Re-read consent rather than trusting detail alone: hasAnalyticsConsent()
      // also honours GPC, which must win over an 'all' click.
      if ((e as CustomEvent).detail === 'all') applyConsentUpgrade();
      else heldReferrer = null; // essential-only is a refusal — drop it now.
    });
  } catch {
    /* no addEventListener (SSR/test harness) — upgrade simply never fires */
  }
}

/**
 * The stored attribution encoded for transport as an HTTP header value.
 * base64 keeps arbitrary UTM text (unicode, punctuation) header-safe.
 * Returns null when there's nothing to send.
 */
export function readAttributionHeader(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    // btoa() is latin1-only, so widen to bytes first — a UTM campaign name
    // with an accent or an em dash would otherwise throw.
    const bytes = new TextEncoder().encode(raw);
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    return btoa(binary);
  } catch {
    return null;
  }
}

/** Drop the stored attribution — called once the server has had a chance to read it. */
export function clearAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clean up */
  }
}
